import type { CandidateBot } from './resolveBot';

// The chat of a version-3 agent (get-demo-agent, spec-007, F5). It is another application
// from the player BotWidgetManager mounts: the chat repository, served from the chat URL this
// bundle is built with, talking to the agent. What the loader does for it is what the chat's
// test host does, read off BotWidgetManager on 02/09/2026: an iframe, marked `data-gd-chat` so
// the chat's page script knows which frame is the chat; that page script, `getdemo-copilot.js`
// from the chat's origin, which runs on THIS page and makes the agent's calls with the person's
// own session; and the messages the chat exchanges with its parent (size, position, identify,
// close). Nothing of the old player is shared: a bot below version 3 never reaches this module,
// and this module never touches BotWidgetManager.

export const NEW_VERSION = 3;
export const PAGE_SCRIPT = 'getdemo-copilot.js';
export const CHAT_MARK = 'data-gd-chat';
export const PAGE_SCRIPT_MARK = 'data-gd-copilot';
// The id the player's iframe has: one bot frame per page, and the loader's own "an embedded
// iframe is present" check sees both alike.
export const IFRAME_ID = 'getdemo-bot-player';
// Above everything the client's page has, the backdrop one below (D6 of the chat's spec-001).
const FRAME_Z_INDEX = '2147483647';
const BACKDROP_Z_INDEX = '2147483646';

export type ChatMode = 'widget' | 'launcher';

export interface MountOptions {
  chatUrl: string;
  botId: string;
  companyId: string;
  mode: ChatMode;
}

// A bot of the new version: `version` comes per bot in the embed-config (decided 03/09/2026);
// absent or below 3 is the old player.
export const isNewVersion = (bot: Pick<CandidateBot, 'version'> | null | undefined): boolean =>
  typeof bot?.version === 'number' && bot.version >= NEW_VERSION;

// The chat's ?mode, with the player's rule: only 'launcher' diverges.
export const chatModeOf = (embedFormat?: string | null): ChatMode =>
  embedFormat === 'launcher' ? 'launcher' : 'widget';

export const chatPageUrl = (
  chatUrl: string,
  botId: string,
  companyId: string,
  mode: ChatMode
): string =>
  `${chatUrl.replace(/\/$/, '')}/b/${encodeURIComponent(
    botId
  )}?mode=${mode}&company=${encodeURIComponent(companyId)}`;

// The page's VISIBLE area and where it sits in the layout. With the keyboard up, a phone keeps
// the layout's height and slides a shorter visible window over it; the chat's full-screen frame
// follows that window, so it is told on every change of size or position.
interface ParentSize {
  width: number;
  height: number;
  top: number;
  left: number;
  layoutHeight: number;
}

// CSS strings the chat asks for its frame, anchor before size; `open` while the person is in
// the conversation (the loader neither swaps nor hides it then); `background` while the chat
// is the whole screen.
interface Frame {
  width?: string;
  height?: string;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  darkenBackground?: boolean;
  open?: boolean;
  background?: string;
}

interface Mounted {
  iframe: HTMLIFrameElement;
  backdrop: HTMLDivElement;
  chatUrl: string;
  botId: string;
  companyId: string;
  mode: ChatMode;
  open: boolean;
}

let mounted: Mounted | null = null;
let identifyParams: Record<string, unknown> = {};
let listening = false;

const parentSize = (): ParentSize => {
  const visible = window.visualViewport;
  const layoutHeight = window.innerHeight;
  return visible
    ? {
        width: visible.width,
        height: visible.height,
        top: visible.offsetTop,
        left: visible.offsetLeft,
        layoutHeight
      }
    : { width: window.innerWidth, height: layoutHeight, top: 0, left: 0, layoutHeight };
};

// To the chat only, at its own origin: identify carries the person's data.
const tell = (message: unknown): void => {
  if (!mounted?.iframe.contentWindow) return;
  mounted.iframe.contentWindow.postMessage(message, new URL(mounted.chatUrl).origin);
};

const tellSize = (): void => tell({ type: 'gd-parent-width', data: parentSize() });

// The page covered while the chat is the whole screen: its canvas (html and body) in the
// chat's colour and the page clipped to the window, so the band a phone's keyboard leaves
// outside any fixed element shows the canvas and not the page. Everything goes back as it was,
// scroll position included, when a frame comes without a colour.
const covered = { on: false, html: '', body: '', overflow: '', height: '', scrollY: 0 };
const coverPage = (color?: string): void => {
  const html = document.documentElement.style;
  const body = document.body.style;
  if (color) {
    if (!covered.on) {
      covered.on = true;
      covered.html = html.background;
      covered.body = body.background;
      covered.overflow = html.overflow;
      covered.height = html.height;
      covered.scrollY = window.scrollY;
    }
    html.background = color;
    body.background = color;
    html.overflow = 'hidden';
    html.height = '100%';
  } else if (covered.on) {
    covered.on = false;
    html.background = covered.html;
    body.background = covered.body;
    html.overflow = covered.overflow;
    html.height = covered.height;
    window.scrollTo(window.scrollX, covered.scrollY);
  }
};

const unmount = (): void => {
  if (!mounted) return;
  mounted.iframe.remove();
  mounted.backdrop.remove();
  coverPage(undefined);
  mounted = null;
};

const applyFrame = (frame: Frame): void => {
  if (!mounted) return;
  const style = mounted.iframe.style;
  for (const key of ['left', 'right', 'top', 'bottom'] as const) {
    const value = frame[key];
    if (value !== undefined) style[key] = value;
  }
  if (frame.width !== undefined) style.width = frame.width;
  if (frame.height !== undefined) style.height = frame.height;
  mounted.open = frame.open === true;
  mounted.backdrop.style.display = frame.darkenBackground ? 'block' : 'none';
  coverPage(frame.background);
  style.opacity = '1';
};

const onMessage = (event: MessageEvent): void => {
  if (!mounted || event.source !== mounted.iframe.contentWindow) return;
  const data = (event.data || {}) as { type?: string; data?: Frame };
  switch (data.type) {
    case 'gd-ready':
      tellSize();
      if (Object.keys(identifyParams).length) {
        tell({ type: 'gd-identify', customParams: identifyParams });
      }
      break;
    case 'gd-request-parent-width':
      tellSize();
      break;
    case 'gd-avatar-resize-reposition':
      applyFrame(data.data || {});
      break;
    case 'gd-avatar-close':
      unmount();
      break;
    default:
  }
};

const listen = (): void => {
  if (listening) return;
  listening = true;
  window.addEventListener('message', onMessage);
  window.addEventListener('resize', tellSize);
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', tellSize);
    window.visualViewport.addEventListener('scroll', tellSize);
  }
};

const unlisten = (): void => {
  if (!listening) return;
  listening = false;
  window.removeEventListener('message', onMessage);
  window.removeEventListener('resize', tellSize);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', tellSize);
    window.visualViewport.removeEventListener('scroll', tellSize);
  }
};

// Once per page: the script guards itself against a second load as well (window.__gdDoor).
const loadPageScript = (chatUrl: string): void => {
  if (document.querySelector(`script[${PAGE_SCRIPT_MARK}]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `${chatUrl.replace(/\/$/, '')}/${PAGE_SCRIPT}`;
  script.setAttribute(PAGE_SCRIPT_MARK, '');
  document.head.appendChild(script);
};

export const isChatMounted = (): boolean => mounted !== null;

export const isChatOpen = (): boolean => Boolean(mounted?.open);

// Who the page says the person is. Sent now when the chat is there, and again on its
// gd-ready, so a late identify and an early one reach it alike.
export const identifyChat = (params: Record<string, unknown>): void => {
  identifyParams = { ...params };
  if (mounted && Object.keys(identifyParams).length) {
    tell({ type: 'gd-identify', customParams: identifyParams });
  }
};

// Reloads the frame to another bot (SPA route change). No-op when the id is unchanged, the
// chat is open, or nothing is mounted — the player's swapBot contract.
export const swapChat = (botId: string, mode: ChatMode): boolean => {
  if (!mounted || botId === mounted.botId || mounted.open) return false;
  mounted.botId = botId;
  mounted.mode = mode;
  mounted.iframe.src = chatPageUrl(mounted.chatUrl, botId, mounted.companyId, mode);
  mounted.iframe.style.opacity = '0';
  return true;
};

// Tears the chat down (no agent eligible on this page). No-op when nothing is mounted;
// skipped while the chat is open, so a conversation is never killed mid-navigation.
export const hideChat = (): boolean => {
  if (!mounted || mounted.open) return false;
  unmount();
  return true;
};

export const mountChat = (options: MountOptions): void => {
  if (mounted) {
    if (mounted.botId !== options.botId || mounted.mode !== options.mode) {
      swapChat(options.botId, options.mode);
    }
    return;
  }
  const backdrop = document.createElement('div');
  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: BACKDROP_Z_INDEX,
    display: 'none',
    pointerEvents: 'none'
  });
  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_ID;
  iframe.title = 'chat';
  iframe.src = chatPageUrl(options.chatUrl, options.botId, options.companyId, options.mode);
  iframe.allow = 'microphone';
  iframe.setAttribute(CHAT_MARK, '');
  Object.assign(iframe.style, {
    position: 'fixed',
    zIndex: FRAME_Z_INDEX,
    border: 'none',
    bottom: '0',
    right: '0',
    opacity: '0'
  });
  document.body.appendChild(backdrop);
  document.body.appendChild(iframe);
  mounted = { iframe, backdrop, ...options, open: false };
  listen();
  loadPageScript(options.chatUrl);
};

// Everything back as before the first mount: the frame, the listeners, the page's canvas and
// what the page said about the person. The page script stays; it is inert without a frame.
export const disposeChat = (): void => {
  unmount();
  unlisten();
  identifyParams = {};
};
